import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchQuestionnaire, fetchPodSchedule } from "../../utils/api";
import Header from "../shared/Header/index";
import "./Assessment.css";
import Loader from "../shared/Loader";
import HomeIcon from "../../assets/icons/home.svg";
import { useAppContext } from "../../context/AppContext";
import { useLocalization } from "../../context/LocalizationContext";
import { convertToArabicNumerals, isPodAvailable } from "../../utils/helpers";
import Modal from "../shared/Modal";

const Assessment = ({ userData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasLoaded = useRef(false);
  const { setCurrentCategory, setCategories, categories } = useAppContext();
  const { t, isRTL } = useLocalization();
  const userDataFromState = location.state?.userData || userData;
  const autoStartAssessment = location.state?.autoStartAssessment;
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

  useEffect(() => {
    if (hasLoaded.current) {
      return;
    }

    const loadAssessments = async () => {
      try {
        hasLoaded.current = true;
        setLoading(true);
        setError(null);

        const [wellness, lifestyle, healthAssessment] = await Promise.all([
          fetchQuestionnaire("WELLNESS"),
          fetchQuestionnaire("LIFESTYLE"),
          fetchQuestionnaire("HEALTH_ASSESSMENT"),
        ]);

        setCategories([
          {
            id: wellness[0]?.id,
            name: "WELLNESS",
            title: wellness[0].name.replace("Assessment", "").trim(),
            description: "Assess your overall wellness and mental health",
            questions: wellness[0]?.questions || [],
            questionCount: wellness[0]?.questions?.length || 0,
          },
          {
            id: lifestyle[0]?.id,
            name: "LIFESTYLE",
            title: lifestyle[0].name.replace("Assessment", "").trim(),
            description: "Evaluate your lifestyle habits and choices",
            questions: lifestyle[0]?.questions || [],
            questionCount: lifestyle[0]?.questions?.length || 0,
          },
          {
            id: healthAssessment[0]?.id,
            name: "HEALTH_ASSESSMENT",
            title: healthAssessment[0].name.replace("Assessment", "").trim(),
            description: "Comprehensive health risk evaluation",
            questions: healthAssessment[0]?.questions || [],
            questionCount: healthAssessment[0]?.questions?.length || 0,
          },
        ]);
      } catch (err) {
        console.error("Error loading assessments:", err);
        setError("Failed to load assessments. Please try again.");
        hasLoaded.current = false;
      } finally {
        setLoading(false);
      }
    };

    loadAssessments();
  }, []);

  useEffect(() => {
    if (autoStartAssessment && categories.length > 0) {
      const nextAssessment = categories.find(
        (cat) => cat.name === autoStartAssessment
      );
      if (nextAssessment) {
        handleAssessmentClick(nextAssessment);
      }
    }
  }, [autoStartAssessment, categories]);

  const handleAssessmentClick = (assessment) => {
    setCurrentCategory(assessment.id);
    navigate(`/assessment/${assessment.id}`, {
      state: {
        assessment,
        userData: userDataFromState,
      },
    });
  };

  const handleSkipAll = async () => {
    try {
      setIsCheckingAvailability(true);
      // Fetch pod schedule (using podId = 1 as default, can be made configurable)
      const scheduleData = await fetchPodSchedule(1, 4, 1, 1);

      // Check if pod is currently available
      const available = isPodAvailable(scheduleData);

      if (available) {
        // Navigate to consultation if available
        navigate("/consultation");
      } else {
        // Show modal if not available
        setShowUnavailableModal(true);
      }
    } catch (error) {
      console.error("Error checking pod availability:", error);
      // On error, show modal to inform user
      setShowUnavailableModal(true);
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  if (loading) {
    return (
      <div className="main-container">
        <Header text={t("assessment.title")} />
        <div className="assessment-content">
          <Loader text={t("loadingAssessments")} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-container">
        <Header text={t("assessment.title")} />
        <div className="assessment-content">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <Header text={t("assessment.title")} userData={userData} />
      <div className="assessment-content">
        <div className="description-container">
          {t("assessment.description")}
        </div>

        <div className="assessments-list">
          {categories.map((assessment) => (
            <div
              key={assessment.id}
              className="assessment-item"
              onClick={() => handleAssessmentClick(assessment)}
            >
              <div className="assessment-info">{assessment.title}</div>
              <div className="assessment-question-count">
                {isRTL
                  ? convertToArabicNumerals(assessment.questionCount)
                  : assessment.questionCount}{" "}
                {t("assessment.questions")}
              </div>
            </div>
          ))}
          <div
            className="assessment-item"
            onClick={handleSkipAll}
            style={{
              opacity: isCheckingAvailability ? 0.6 : 1,
              pointerEvents: isCheckingAvailability ? 'none' : 'auto'
            }}
          >
            <div className="assessment-info">
              {isCheckingAvailability
                ? t("consultation.checkingAvailability")
                : t("assessment.skipAll")}
            </div>
            <div className="assessment-question-count">
              {t("assessment.talkToDoctor")}
            </div>
          </div>
        </div>
      </div>

      <div className="assessment-footer">
        <button
          className="footer-btn home-btn"
          onClick={() => navigate("/dashboard")}
        >
          <img src={HomeIcon} alt="Home" />
        </button>
      </div>
      <Modal
        isOpen={showUnavailableModal}
        onClose={() => setShowUnavailableModal(false)}
        title={t("consultation.notAvailable")}
        message={t("consultation.notAvailableMessage")}
      />
    </div>
  );
};

export default Assessment;
